import { Injectable } from '@angular/core';
import { Geolocation } from '@capacitor/geolocation';
import { AlertController } from '@ionic/angular';
import { Preferences } from '@capacitor/preferences';
import { Logger } from '../logger.service';

const log = new Logger('location-service');

export interface ApiLocation {
  latitude: number;
  longitude: number;
}

@Injectable({
  providedIn: 'root',
})
export class LocationService {
  private path = 'location-data';

  private mexicoPolygon: [number, number][] = [
    [32.7638099, -114.6775204],
    [32.5788484, -114.8752743],
    [31.3298017, -111.0300594],
    [31.3485686, -108.2395321],
    [31.8538561, -108.1955868],
    [31.7604914, -106.4158016],
    [30.7084015, -104.9436337],
    [29.7973678, -104.5261532],
    [29.0317716, -103.2077938],
    [29.8354958, -102.746368],
    [29.911708, -102.2629696],
    [29.8354958, -101.406036],
    [29.2812225, -100.636993],
    [28.2604551, -100.2195126],
    [27.5956533, -99.4066519],
    [26.4604537, -99.0111441],
    [26.0071387, -97.2093863],
    [24.3968437, -97.6048941],
    [22.5832892, -97.5609488],
    [19.1137291, -95.7811636],
    [18.3855035, -94.221105],
    [19.2589943, -91.3646597],
    [21.50389, -90.3978628],
    [21.6877621, -86.2670035],
    [18.5105645, -87.2118277],
    [18.5522312, -88.5082144],
    [17.8843569, -88.9037222],
    [17.6751252, -89.1893667],
    [17.7797716, -90.9471792],
    [17.2139609, -90.9471792],
    [17.1719793, -91.2108511],
    [16.4357805, -90.3758902],
    [16.0349496, -90.4418081],
    [16.0349496, -91.7162222],
    [15.252083, -92.1337027],
    [15.252083, -92.1337027],
    [14.4024517, -92.2215933],
    [15.9927095, -94.6605581],
    [15.3792368, -96.4403433],
    [17.9679807, -103.6254019],
    [18.8644063, -105.2733511],
    [20.3340278, -105.8006949],
    [22.7454962, -106.17423],
    [22.156589, -109.9095816],
    [24.7166069, -112.9637808],
    [27.7513266, -116.1058706],
    [32.7638099, -114.6775204],
  ];

  constructor(private alertCtr: AlertController) {}

  private async checkPermissions() {
    const perm = await Geolocation.checkPermissions();
    return perm.coarseLocation;
  }

  async getLocation(force: boolean): Promise<ApiLocation | null> {
    if (!force) {
      const coords = await Preferences.get({
        key: this.path,
      });

      if (coords.value) {
        return JSON.parse(coords.value);
      }
    }

    let permission = await this.checkPermissions();

    if (permission.includes('prompt')) {
      const status = await Geolocation.requestPermissions({
        permissions: ['location'],
      });

      permission = status.coarseLocation;
    }

    if (permission === 'denied') {
      const alert = await this.alertCtr.create({
        header: 'Permiso de ubicación desactivado',
        subHeader:
          'Para continuar, necesitamos que actives el acceso a tu ubicación aproximada desde la configuración del dispositivo. Esto es esencial para garantizar tu seguridad y el correcto funcionamiento de la app.',
        buttons: ['Ok'],
      });

      await alert.present();
    }

    if (permission === 'granted') {
      const currentPosition = await Geolocation.getCurrentPosition({
        timeout: 23000,
        enableHighAccuracy: true,
      });

      const { latitude, longitude } = currentPosition.coords;
      await Preferences.set({
        key: this.path,
        value: JSON.stringify({ latitude, longitude }),
      });

      log.info(JSON.stringify({ latitude, longitude }, null, 2));

      return { latitude, longitude };
    }

    return null;
  }

  private isPointInMexico(lat: number, lon: number): boolean {
    let inside = false;

    for (let i = 0, j = this.mexicoPolygon.length - 1; i < this.mexicoPolygon.length; j = i++) {
      const xi = this.mexicoPolygon[i][0],
        yi = this.mexicoPolygon[i][1];
      const xj = this.mexicoPolygon[j][0],
        yj = this.mexicoPolygon[j][1];

      const intersect = yi > lon !== yj > lon && lat < ((xj - xi) * (lon - yi)) / (yj - yi) + xi;
      if (intersect) inside = !inside;
    }

    return inside;
  }

  async isMx() {
    const coord = await this.getLocation(false);
    if (coord) return this.isPointInMexico(coord.latitude, coord.longitude);
    return false;
  }
}
